/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, IconButton, Box, Typography,
    Grid, TextField, InputAdornment,
    CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { requestSignalProjectMonthlyDetail, requestSignalProjectMonthlyResources } from '../../services/signalApi';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { ProjectGraphCard } from './ProjectGraphCard';
import { ProjectResourceCard } from './ProjectResourceCard';
import { resetGraphModal, setGraphModalResources } from '../../store/slices/projects/projectsSlice';
import { useDispatch } from 'react-redux';
import { Pagination } from '@mui/material';

interface ProjectGraphModalProps {
    open: boolean;
    onClose: () => void;
    projectId: string;
    typeCode: string;
    typeId: string;
    head: string;
    selectedDate: string;
    selectedScale: '0' | '1' | '2';
}

export const ProjectGraphModal: React.FC<ProjectGraphModalProps> = ({
    open, onClose, projectId, typeCode, typeId, head, selectedDate, selectedScale
}) => {
    const dispatch = useDispatch();
    const { graphModal } = useSelector((state: RootState) => state.projects);

    const [view, setView] = useState<'list' | 'detail'>('list');
    const [detailMode, setDetailMode] = useState<'period' | 'accumulated'>('period');
    const [selectedItem, setSelectedItem] = useState<any>(null);

    const [listPage, setListPage] = useState(1);
    const [resourcePage, setResourcePage] = useState(1);

    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    const [isListLoading, setIsListLoading] = useState(false);
    const [isResourceLoading, setIsResourceLoading] = useState(false);

    const fetchedQueryParams = React.useRef<string>('');
    const fetchedResourceParams = React.useRef<string>('');

    const topRef = React.useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) {
            fetchedQueryParams.current = '';
            fetchedResourceParams.current = '';
        }
    }, [open]);

    useEffect(() => {
        if (open) {
            setView('list');
            setListPage(1);
            setResourcePage(1);

            fetchedQueryParams.current = '';
            fetchedResourceParams.current = '';
            setIsListLoading(false);
            setIsResourceLoading(false);

            dispatch(resetGraphModal());
            console.log('prev fetchList 1 head', head)
            fetchList(1);
        } else {
            setIsListLoading(false);
            setIsResourceLoading(false);
        }
    }, [open, projectId, typeCode, typeId, selectedDate]);

    useEffect(() => {
        if (isListLoading && graphModal.list.length > 0) {
            setIsListLoading(false);
        }
    }, [graphModal.list, isListLoading]);

    useEffect(() => {
        if (isResourceLoading && graphModal.resources.length > 0) {
            setIsResourceLoading(false);
        }
    }, [graphModal.resources, isResourceLoading]);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(search);
        }, 800);
        return () => clearTimeout(handler);
    }, [search]);

    useEffect(() => {
        if (view === 'detail' && selectedItem) {
            dispatch(setGraphModalResources({ count: 0, data: [] }));
            setResourcePage(1);
            fetchedResourceParams.current = '';
            fetchResources(selectedItem, detailMode, 1, debouncedSearch);
        }
    }, [debouncedSearch, view, selectedItem, detailMode]);

    const handleListPageChange = (event: React.ChangeEvent<unknown>, value: number) => {
        setListPage(value);
        console.log('prev fetchList 2 head', head)
        fetchList(value);
        topRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const handleResourcePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
        setResourcePage(value);
        fetchResources(selectedItem, detailMode, value, debouncedSearch);
        topRef.current?.scrollIntoView({ behavior: 'smooth' });
    };



    const fetchList = async (pageNum: number) => {
        if (isListLoading) return;

        const queryKey = `${projectId}-${typeCode}-${typeId}-${pageNum}-${selectedDate}`;
        if (fetchedQueryParams.current === queryKey) return;

        setIsListLoading(true);
        fetchedQueryParams.current = queryKey;

        try {
            await requestSignalProjectMonthlyDetail(projectId, typeCode, typeId, head, graphModal.itemsPerPage, pageNum, selectedDate, selectedScale);
        } catch (error) {
            console.error(error);
            fetchedQueryParams.current = '';
            setIsListLoading(false);
        }
    };

    const fetchResources = async (item: any, mode: 'period' | 'accumulated', pageNum: number, searchText: string) => {
        if (isResourceLoading) return;

        const accumulatedVal = mode === 'accumulated' ? 1 : 0;
        const queryKey = `${projectId}-${typeCode}-${selectedDate}-${accumulatedVal}-${pageNum}-${searchText}`;

        if (fetchedResourceParams.current === queryKey) return;

        setIsResourceLoading(true);
        fetchedResourceParams.current = queryKey;

        try {
            await requestSignalProjectMonthlyResources(
                projectId, 
                typeCode, 
                selectedDate, 
                typeId,
                head,
                graphModal.itemsPerPage, 
                pageNum, 
                accumulatedVal, 
                searchText,
                selectedScale,
            );
            setIsResourceLoading(false);
        } catch (error) {
            console.error(error);
            fetchedResourceParams.current = '';
            setIsResourceLoading(false);
        }
    };

    const handleItemClick = (item: any, mode: 'period' | 'accumulated') => {
        console.log('---- handleItemClick 1')
        setSelectedItem(item);
        setDetailMode(mode);
        setView('detail');
        setResourcePage(1);
        setSearch('');
        fetchedResourceParams.current = '';
        console.log('---- handleItemClick 2')
        fetchResources(item, mode, 1, '');
    };

    const handleBack = () => {
        setView('list');
        setSelectedItem(null);
    };



    const handleModeSwitch = (mode: 'period' | 'accumulated') => {
        if (detailMode === mode) return;
        setDetailMode(mode);
        setResourcePage(1);
        setSearch('');
        fetchedResourceParams.current = '';
        fetchResources(selectedItem, mode, 1, '');
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            sx={{
                '& .MuiDialog-paper': {
                    height: 'calc(100vh - 64px)',
                    maxHeight: 'calc(100vh - 64px)',
                }
            }}
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 'bold' }}>
                RO - {head} Período y Acumulado (S/.)
                <IconButton onClick={onClose}><CloseIcon /></IconButton>
            </DialogTitle>
            <DialogContent sx={{ bgcolor: '#f5f5f5', p: 2 }}>
                <div ref={topRef} />

                {isListLoading && <Box display="flex" justifyItems="center" p={4}><CircularProgress /></Box>}

                <Box sx={{ display: view === 'list' ? 'block' : 'none' }}>
                    {!isListLoading && (
                        <>
                            {graphModal.listPages > 1 && (
                                <Box mb={2} display="flex" justifyContent="center">
                                    <Pagination
                                        count={graphModal.listPages}
                                        page={listPage}
                                        onChange={handleListPageChange}
                                        variant="outlined"
                                        shape="rounded"
                                        sx={{ '& .Mui-selected': { borderColor: 'primary.main', color: 'primary.main', backgroundColor: 'transparent' } }}
                                    />
                                </Box>
                            )}

                            <Grid container spacing={2}>
                                {graphModal.list.map((item, index) => (
                                    <Grid item xs={12} key={index}>
                                        <ProjectGraphCard
                                            item={item}
                                            onPeriodClick={() => handleItemClick(item, 'period')}
                                            onAccumulatedClick={() => handleItemClick(item, 'accumulated')}
                                        />
                                    </Grid>
                                ))}
                            </Grid>

                            {graphModal.listPages > 1 && (
                                <Box mt={2} display="flex" justifyContent="center">
                                    <Pagination
                                        count={graphModal.listPages}
                                        page={listPage}
                                        onChange={handleListPageChange}
                                        variant="outlined"
                                        shape="rounded"
                                        sx={{ '& .Mui-selected': { borderColor: 'primary.main', color: 'primary.main', backgroundColor: 'transparent' } }}
                                    />
                                </Box>
                            )}
                        </>
                    )}
                </Box>

                {view === 'detail' && selectedItem && (
                    <Box>
                        <Box>
                            <ProjectGraphCard
                                item={selectedItem}
                                onBack={handleBack}
                                selectedMode={detailMode}
                                onPeriodClick={() => handleModeSwitch('period')}
                                onAccumulatedClick={() => handleModeSwitch('accumulated')}
                            />
                        </Box>
                        <Box mt={2}>
                            <Box display="flex" justifyContent="space-between" mb={2}>
                                <TextField
                                    size="small"
                                    placeholder="Buscar recurso..."
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
                                    sx={{ width: 300, bgcolor: 'white' }}
                                />
                                <Typography variant="caption" color="textSecondary">
                                    Página {resourcePage}
                                </Typography>
                            </Box>

                            {graphModal.resourcesPages > 1 && (
                                <Box mb={2} display="flex" justifyContent="center">
                                    <Pagination
                                        count={graphModal.resourcesPages}
                                        page={resourcePage}
                                        onChange={handleResourcePageChange}
                                        variant="outlined"
                                        shape="rounded"
                                        sx={{ '& .Mui-selected': { borderColor: 'primary.main', color: 'primary.main', backgroundColor: 'transparent' } }}
                                    />
                                </Box>
                            )}

                            <Grid container spacing={1}>
                                {graphModal.resources.map((row: any, i: number) => (
                                    <Grid item xs={12} key={i}>
                                        <ProjectResourceCard item={row} />
                                    </Grid>
                                ))}
                            </Grid>

                            {graphModal.resourcesPages > 1 && (
                                <Box mt={2} display="flex" justifyContent="center">
                                    <Pagination
                                        count={graphModal.resourcesPages}
                                        page={resourcePage}
                                        onChange={handleResourcePageChange}
                                        variant="outlined"
                                        shape="rounded"
                                        sx={{ '& .Mui-selected': { borderColor: 'primary.main', color: 'primary.main', backgroundColor: 'transparent' } }}
                                    />
                                </Box>
                            )}

                        </Box>
                    </Box>
                )}
            </DialogContent>
        </Dialog >
    );
}

